import React, { useRef, useState } from 'react';
import './Contact.css';
import { sendEvent } from '../analytics';
import { useSectionView } from '../hooks/useSectionView';

const Contact = () => {
  const [formData, setFormData] = useState({
    naam: '',
    email: '',
    telefoon: '',
    organisatie: '',
    bericht: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [copySuccess, setCopySuccess] = useState('');

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`${type} gekopieerd!`);
      console.log(`${type} copied to clipboard: ${text}`);
      try { sendEvent('copy_contact_detail', { field: (type || '').toLowerCase() }); } catch (_) {}
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setCopySuccess('');
      }, 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopySuccess(`${type} gekopieerd!`);
      setTimeout(() => {
        setCopySuccess('');
      }, 3000);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const ScrollArrow = ({ targetSection, className }) => (
    <div 
      className={`scroll-arrow ${className || ''}`}
      onClick={() => scrollToSection(targetSection)}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path 
          d="M7 10L12 15L17 10" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    const form = e.currentTarget;

    try {
      const fd = new FormData(form);
      if (!fd.get('form-name')) fd.set('form-name', 'contact');

      const body = new URLSearchParams(fd).toString();
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      
      if (!res.ok) throw new Error("Network error");

      form.reset();
      setStatus('success');
      setIsSubmitted(true);
      
      // Reset form na 3 seconden
      setTimeout(() => {
        setIsSubmitted(false);
        setStatus('idle');
        setFormData({
          naam: '',
          email: '',
          telefoon: '',
          organisatie: '',
          bericht: ''
        });
      }, 3000);
    } catch (err) {
      console.error(err);
      setStatus('error');
      
      // Reset error status na 5 seconden
      setTimeout(() => {
        setStatus('idle');
      }, 5000);
    }
  };

  const sectionRef = useRef(null);

  useSectionView('contact', 'contact-section');

  return (
    <div ref={sectionRef} id="contact-section" className="contact-section">
      {/* Scroll Up Arrow */}
      <div 
        className="scroll-arrow scroll-up-arrow"
        onClick={() => {
          console.log('Scroll up arrow clicked');
          try {
            // Try multiple scroll methods
            window.scrollTo({ 
              top: 0, 
              left: 0,
              behavior: 'smooth' 
            });
            
            // Fallback method
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
          } catch (error) {
            console.error('Scroll error:', error);
          }
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M17 14L12 9L7 14" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Copy Success Toast */}
      {copySuccess && (
        <div className="copy-toast">
          <svg className="toast-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="8" fill="rgba(255, 255, 255, 0.3)"/>
            <path d="M6 10l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{copySuccess}</span>
        </div>
      )}
      
      <div className="contact-container">
        <div className="contact-header">
          <h2 className="contact-title">Neem contact op</h2>
          <p className="contact-subtitle">
            Vragen over onze dienstverlening of wilt u een kennismakingsgesprek? 
            Vul onderstaand formulier in en wij nemen zo spoedig mogelijk contact op.
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <div className="info-item" onClick={() => copyToClipboard('Euclideslaan 60, 3584 BN Utrecht, Nederland', 'Adres')}>
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="#18191C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="10" r="3" stroke="#18191C" strokeWidth="2"/>
                </svg>
              </div>
              <div className="info-text">
                <h4>Adres</h4>
                <p>Euclideslaan 60<br />3584 BN Utrecht</p>
              </div>
              <div className="copy-icon-container">
                <svg className="copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85952 1.72386 9.60948C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60948 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* <div className="info-item" onClick={() => copyToClipboard('033 - 123 45 67', 'Telefoonnummer')}>
              <div className="info-icon"> 
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="#18191C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg> 
              </div> 
               <div className="info-text">
                <h4>Telefoon</h4>
                <p></p>
              </div>
              <div className="copy-icon-container">
                <svg className="copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85952 1.72386 9.60948C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60948 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div> */}

            <div className="info-item" onClick={() => copyToClipboard('info@waardewerkers.nl', 'E-mail')}>
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#18191C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22,6 12,13 2,6" stroke="#18191C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="info-text">
                <h4>E-mail</h4>
                <p>info@waardewerkers.nl</p>
              </div>
              <div className="copy-icon-container">
                <svg className="copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85952 1.72386 9.60948C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60948 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div className="info-item" onClick={() => copyToClipboard('123456', 'KVK')}>
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" stroke="#18191C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="3" y="7" width="18" height="13" rx="2" stroke="#18191C" strokeWidth="2"/>
                  <path d="M3 12h18" stroke="#18191C" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="info-text">
                <h4>KVK</h4>
                <p>97996165</p>
              </div>
              <div className="copy-icon-container">
                <svg className="copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85952 1.72386 9.60948C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60948 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            {isSubmitted ? (
              <div className="success-message">
                <div className="success-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="20" fill="#10B981"/>
                    <path d="M16 24l6 6 12-12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Bedankt voor uw bericht!</h3>
                <p>Wij hebben uw gegevens ontvangen en nemen zo spoedig mogelijk contact met u op.</p>
              </div>
            ) : (
              <form 
                className="contact-form" 
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
              >
                <input type="hidden" name="form-name" value="contact" />
                <input 
                  type="text" 
                  name="bot-field" 
                  aria-hidden="true" 
                  tabIndex={-1} 
                  style={{ position: "absolute", left: "-9999px" }} 
                />
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="naam">Naam *</label>
                    <input
                      type="text"
                      id="naam"
                      name="naam"
                      value={formData.naam}
                      onChange={handleChange}
                      required
                      placeholder="Uw volledige naam"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">E-mailadres *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="uw.email@voorbeeld.nl"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="telefoon">Telefoonnummer</label>
                    <input
                      type="tel"
                      id="telefoon"
                      name="telefoon"
                      value={formData.telefoon}
                      onChange={handleChange}
                      placeholder="06 - 12 34 56 78"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="organisatie">Organisatie</label>
                    <input
                      type="text"
                      id="organisatie"
                      name="organisatie"
                      value={formData.organisatie}
                      onChange={handleChange}
                      placeholder="Uw organisatie of bedrijf"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="bericht">Bericht *</label>
                  <textarea
                    id="bericht"
                    name="bericht"
                    value={formData.bericht}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Beschrijf uw vraag of project..."
                  ></textarea>
                </div>

                <button type="submit" className="submit-button" disabled={status === 'submitting'}>
                  <span>{status === 'submitting' ? 'Versturen...' : 'Verstuur bericht'}</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 2L9 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 2l-7 16-4-7-7-4 18-5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                {status === 'error' && (
                  <p role="alert" style={{ color: '#ef4444', marginTop: '1rem', textAlign: 'center' }}>
                    Er ging iets mis. Probeer het later opnieuw.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
      <ScrollArrow targetSection="footer-section" className="contact-arrow" />
    </div>
  );
};

export default Contact;
